// The user's own subscriptions (each person brings their own) and the state of the inference services.
import { useEffect, useState } from "react";
import { api, type ApiKey, type Me, type SecretT, type Service } from "./api";

export function Settings({ me, onChange }: { me: Me; onChange: () => void }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [services, setServices] = useState<Service[] | null>(null);
  const [keysShown, setKeysShown] = useState(0);          // a phone paired: the key list loads again
  useEffect(() => { api.services().then(setServices).catch(() => setServices([])); }, []);

  const connect = () => {
    setBusy(true); setErr("");
    api.connectClaude(token.trim()).then(() => { setToken(""); onChange(); })
      .catch((e) => setErr(String(e.message))).finally(() => setBusy(false));
  };

  return (
    <div className="page"><div className="page-inner" style={{ maxWidth: 680 }}>
      <h1>Settings</h1>
      <p className="lede">The agent runs on your own subscriptions. Nothing here is shared with other users.</p>

      <h2>Claude</h2>
      {me.claude.connected ? (
        <div className="kv">
          <span className="label">Status</span>
          <span><span className="dot ok" /> Connected · token {me.claude.hint} · since {new Date((me.claude.since ?? 0) * 1000).toLocaleDateString()}</span>
          <span />
          <span><button className="btn quiet danger" onClick={() => api.disconnectClaude().then(onChange)}>Disconnect</button></span>
        </div>
      ) : (
        <>
          <p className="note">On your own computer, run <span className="mono">claude setup-token</span>, sign in with your
            Claude subscription, and paste the token it prints (sk-ant-oat01-…). It is checked with one short message, then
            stored for your account only.</p>
          <div className="form" style={{ gridTemplateColumns: "1fr auto" }}>
            <input className="field mono" type="password" autoComplete="off" placeholder="sk-ant-oat01-…" value={token}
              onChange={(e) => setToken(e.target.value)} onKeyDown={(e) => e.key === "Enter" && token && connect()} />
            <button className="btn primary" disabled={!token.trim() || busy} onClick={connect}>{busy ? "Checking…" : "Connect"}</button>
          </div>
        </>
      )}
      {err && <div className="error-line">{err}</div>}

      <h2>Codex</h2>
      <Codex me={me} onChange={onChange} />

      <h2>iPhone app</h2>
      <IPhonePairing onPaired={() => setKeysShown((n) => n + 1)} />

      <h2>API keys</h2>
      <ApiKeys key={keysShown} />

      <h2>Secrets</h2>
      <Secrets />

      <h2>Services</h2>
      <table className="grid">
        <thead><tr><th>Service</th><th>Status</th><th>Answer</th></tr></thead>
        <tbody>
          {services === null && <tr><td colSpan={3} className="muted">Checking…</td></tr>}
          {services?.map((s) => (
            <tr key={s.name}>
              <td>{s.name}</td>
              <td><span className={`dot ${!s.configured ? "" : s.ok ? "ok" : "err"}`} /> {!s.configured ? "off" : s.ok ? "up" : "down"}</td>
              <td className="muted">{s.ms != null ? `${Math.round(s.ms)} ms` : s.error}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}

// ChatGPT for Codex: the server starts `codex login --device-auth` for this user and shows its link and one-time code;
// the user signs in on OpenAI's own page, in their own browser. Nothing about the ChatGPT account passes through here.
function Codex({ me, onChange }: { me: Me; onChange: () => void }) {
  const [st, setSt] = useState(me.codex);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setSt(me.codex); }, [me.codex]);
  useEffect(() => {
    if (!st.pending) return;
    const t = setInterval(() => api.codexState().then((s) => {
      setSt(s);
      if (s.connected) onChange();
      if (s.error) setErr(s.error);
    }).catch(() => undefined), 2000);
    return () => clearInterval(t);
  }, [st.pending, onChange]);

  const start = () => {
    setBusy(true); setErr("");
    api.connectCodex().then((p) => setSt({ ...st, pending: p })).catch((e) => setErr(String(e.message))).finally(() => setBusy(false));
  };
  if (st.available === false) return <p className="note">Codex is not installed on this server.</p>;
  if (st.connected) return (
    <div className="kv">
      <span className="label">Status</span>
      <span><span className="dot ok" /> Connected to ChatGPT{st.since ? ` · since ${new Date(st.since * 1000).toLocaleDateString()}` : ""}</span>
      <span /><span><button className="btn quiet danger" onClick={() => api.disconnectCodex().then(onChange)}>Disconnect</button></span>
    </div>
  );
  return (
    <>
      {!st.pending ? (
        <>
          <p className="note">Codex runs on your own ChatGPT plan. Connecting shows a link and a one-time code: open
            the link, sign in to ChatGPT there, and enter the code.</p>
          <button className="btn primary" disabled={busy} onClick={start}>{busy ? "Starting…" : "Connect ChatGPT"}</button>
        </>
      ) : (
        <div className="device">
          <div className="kv">
            <span className="label">1. Open</span>
            <span><a href={st.pending.url} target="_blank" rel="noopener noreferrer">{st.pending.url}</a></span>
            <span className="label">2. Enter</span>
            <span className="device-code mono">{st.pending.code}</span>
            <span className="label">Status</span>
            <span><span className="spin" style={{ display: "inline-block", verticalAlign: -1 }} /> Waiting for you to sign in · code
              expires {new Date(st.pending.expires * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        </div>
      )}
      {err && <div className="error-line">{err}</div>}
    </>
  );
}

// The iPhone app pairs by QR code: the code in it is good for one pairing within 5 minutes and is not a key; the
// phone trades it for a key of its own, which then shows under API keys (revoking it there unpairs the phone).
function IPhonePairing({ onPaired }: { onPaired: () => void }) {
  const [phones, setPhones] = useState<{ id: string; device: string | null; created_at: number; last_used: number | null }[]>([]);
  const loadPhones = () => api.phones().then(setPhones).catch(() => undefined);
  useEffect(() => { loadPhones(); }, []);
  const [p, setP] = useState<{ id: string; expires_at: number; qr: string } | null>(null);
  const [state, setState] = useState<{ state: string; device: string | null } | null>(null);
  const [now, setNow] = useState(Date.now() / 1000);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!p || state?.state === "paired" || state?.state === "expired") return;
    const t = setInterval(() => {
      setNow(Date.now() / 1000);
      api.pairing(p.id).then((s) => { setState(s); if (s.state === "paired") { onPaired(); loadPhones(); } }).catch(() => undefined);
    }, 1500);
    return () => clearInterval(t);
  }, [p, state?.state, onPaired]);
  const show = () => {
    setErr(""); setState(null);
    api.newPairing().then((r) => { setP(r); setNow(Date.now() / 1000); }).catch((e) => setErr(String(e.message)));
  };
  const left = p ? Math.max(0, Math.round(p.expires_at - now)) : 0;
  const expired = state?.state === "expired" || (p !== null && left === 0 && state?.state !== "paired");
  return (
    <>
      <p className="note">Drive your computers from the Voitta iPhone app. Show a pairing code and scan it with the
        iPhone's camera or in the app. The code works once, for 5 minutes; the phone gets a key of its own, listed
        under API keys, where revoking it unpairs the phone.</p>
      {p && state?.state === "paired" ? (
        <div className="kv">
          <span className="label">Status</span>
          <span><span className="dot ok" /> Paired with {state.device || "an iPhone"}</span>
          <span /><span><button className="btn quiet" onClick={() => { setP(null); setState(null); }}>Done</button></span>
        </div>
      ) : p && !expired ? (
        <div className="pairing">
          <img className="pairing-qr" src={p.qr} alt="Pairing code for the Voitta iPhone app" width={232} height={232} />
          <div className="kv">
            <span className="label">1. Open</span><span>the Camera on the iPhone, or Voitta → Scan pairing code</span>
            <span className="label">2. Point</span><span>at this code; the app opens and pairs</span>
            <span className="label">Status</span>
            <span><span className="spin" style={{ display: "inline-block", verticalAlign: -1 }} /> Waiting for the
              phone · {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")} left</span>
            <span /><span><button className="btn quiet" onClick={() => { setP(null); setState(null); }}>Cancel</button></span>
          </div>
        </div>
      ) : (
        <>
          {p && expired && <div className="note">That code has expired.</div>}
          <button className="btn primary" onClick={show}>Show pairing code</button>
        </>
      )}
      {phones.length > 0 && (
        <table className="grid">
          <thead><tr><th>Paired phone</th><th>Paired</th><th>Last used</th><th /></tr></thead>
          <tbody>
            {phones.map((ph) => (
              <tr key={ph.id}>
                <td>{ph.device || "iPhone"}</td>
                <td className="muted">{new Date(ph.created_at * 1000).toLocaleDateString()}</td>
                <td className="muted">{ph.last_used ? new Date(ph.last_used * 1000).toLocaleDateString() : "never"}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn quiet danger" onClick={() => confirm(`Unpair ${ph.device || "this iPhone"}? The app on it signs out.`)
                    && api.revokeKey(ph.id).then(() => { loadPhones(); onPaired(); }).catch((e) => setErr(String(e.message)))}>Unpair</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {err && <div className="error-line">{err}</div>}
    </>
  );
}

// API keys: a person's own Claude Code drives their computer over MCP (/api/mcp). A new key is shown once, with the
// ready-made `claude mcp add` line; the server keeps only its hash. Claude Code drives the computer last connected to
// here, and the chat shows what it does.
function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [name, setName] = useState("");
  const [made, setMade] = useState<{ secret: string; setup: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.keys().then(setKeys).catch((e) => setErr(String(e.message)));
  useEffect(() => { load(); }, []);
  const create = () => {
    setErr(""); setCopied(false);
    api.newKey(name.trim() || "Claude Code").then((r) => { setMade({ secret: r.secret, setup: r.setup }); setName(""); load(); })
      .catch((e) => setErr(String(e.message)));
  };
  const copy = () => {
    if (!made) return;
    navigator.clipboard?.writeText(made.setup).then(() => setCopied(true)).catch(() => setCopied(false));
  };
  const day = (t: number | null) => (t ? new Date(t * 1000).toLocaleDateString() : "never");
  return (
    <>
      <p className="note">Your own Claude Code can drive the computer you last connected to here, over MCP, on your own
        Claude plan. The chat shows what it does, and you can stop or take over at any time.</p>
      {made && (
        <div className="key-made">
          <div className="label">Run this once in a terminal. The key is shown only now.</div>
          <code className="mono key-line">{made.setup}</code>
          <div className="key-actions">
            <button className="btn primary" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
            <button className="btn quiet" onClick={() => setMade(null)}>Done</button>
          </div>
        </div>
      )}
      {keys && keys.length > 0 && (
        <table className="grid">
          <thead><tr><th>Name</th><th>Key</th><th>Made</th><th>Last used</th><th /></tr></thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td><td className="mono muted">{k.prefix}…</td>
                <td className="muted">{day(k.created_at)}</td><td className="muted">{day(k.last_used)}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn quiet danger" onClick={() => confirm(`Revoke "${k.name}"? Claude Code using it stops working.`)
                    && api.revokeKey(k.id).then(load).catch((e) => setErr(String(e.message)))}>Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="form" style={{ gridTemplateColumns: "1fr auto" }}>
        <input className="field" placeholder="Name, e.g. laptop" value={name} maxLength={80}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
        <button className="btn" onClick={create}>New key</button>
      </div>
      {err && <div className="error-line">{err}</div>}
    </>
  );
}

// Secrets: passwords and PINs the agent types by name (its type_secret tool) without ever seeing them. A value is
// write-only: the server never sends it back, so editing a secret without a new value keeps the stored one.
function Secrets() {
  const [list, setList] = useState<SecretT[] | null>(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [value, setValue] = useState("");
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.secrets().then(setList).catch((e) => setErr(String(e.message))); }, []);
  const reset = () => { setName(""); setDesc(""); setValue(""); setEditing(false); };
  const save = () => {
    setErr("");
    api.saveSecret({ name: name.trim().toLowerCase(), description: desc.trim(), value })
      .then((l) => { setList(l); reset(); }).catch((e) => setErr(String(e.message)));
  };
  const edit = (s: SecretT) => { setName(s.name); setDesc(s.description); setValue(""); setEditing(true); setErr(""); };
  return (
    <>
      <p className="note">Passwords and PINs the agent can type for you by name. It sees only the name and description,
        never the value; the value is typed on the computer by the KVM and kept out of every log. Say in the description
        where it belongs, e.g. “Jira login password”. These are yours, on every computer you use; a value for one
        computer only belongs under that computer's Values &amp; secrets, on the Computers page, where it wins over one
        of yours with the same name.</p>
      {list && list.length > 0 && (
        <table className="grid">
          <thead><tr><th>Name</th><th>Description</th><th>Changed</th><th /></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.name}>
                <td className="mono">{s.name}</td><td>{s.description || <span className="muted">—</span>}</td>
                <td className="muted">{new Date(s.updated_at * 1000).toLocaleDateString()}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <button className="btn quiet" onClick={() => edit(s)}>Change</button>
                  <button className="btn quiet danger" onClick={() => confirm(`Remove "${s.name}"?`)
                    && api.removeSecret(s.name).then(setList).catch((e) => setErr(String(e.message)))}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="form secret-form">
        <input className="field mono" placeholder="Name, e.g. jira" value={name} maxLength={40} readOnly={editing}
          onChange={(e) => setName(e.target.value)} />
        <input className="field" placeholder="Where it belongs" value={desc} maxLength={200}
          onChange={(e) => setDesc(e.target.value)} />
        <input className="field mono" type="password" autoComplete="new-password" value={value}
          placeholder={editing ? "New value (empty: keep)" : "Value"}
          onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && save()} />
        <span style={{ display: "flex", gap: 6 }}>
          <button className="btn" disabled={!name.trim() || (!editing && !value)} onClick={save}>{editing ? "Save" : "Add"}</button>
          {editing && <button className="btn quiet" onClick={reset}>Cancel</button>}
        </span>
      </div>
      {err && <div className="error-line">{err}</div>}
    </>
  );
}
